import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiResponse } from '@core/models/api-response.interface';
import { WorkspaceDto } from '@core/models/workspace.interface';
import { environment } from 'environments/environment';
import { BehaviorSubject, finalize, tap } from 'rxjs';

const WORKSPACE_URL = `${environment.apiBaseUrl}workspaces`;
const WORKSPACE_DETAILS_KEY = 'workspaceDetails';

export interface UpdateWorkspaceProfilePayload {
  name?: string;
  email?: string;
  phone?: string;
  url?: string;
  urlDisplayName?: string;
}

@Injectable({
  providedIn: 'root',
})
export class WorkspaceService {
  http = inject(HttpClient);
  isLoadingSubject = new BehaviorSubject<boolean>(false);
  isLoading$ = this.isLoadingSubject.asObservable();
  workspaceDetailsSubject = new BehaviorSubject<WorkspaceDto | null>(
    this.getWorkspaceFromStorage(),
  );
  workspaceDetails$ = this.workspaceDetailsSubject.asObservable();

  GetWorkspaceDetails() {
    this.isLoadingSubject.next(true);
    return this.http
      .get<ApiResponse<WorkspaceDto>>(WORKSPACE_URL)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  fetchAndStoreWorkspaceDetails() {
    return this.GetWorkspaceDetails().pipe(
      tap((res) => {
        if (res?.success && res?.data) {
          this.setWorkspace(res.data);
        }
      }),
    );
  }

  setWorkspace(workspace: WorkspaceDto) {
    localStorage.setItem(WORKSPACE_DETAILS_KEY, JSON.stringify(workspace));
    this.workspaceDetailsSubject.next(workspace);
  }

  getWorkspaceFromStorage(): WorkspaceDto | null {
    const workspace = localStorage.getItem(WORKSPACE_DETAILS_KEY);
    return workspace ? JSON.parse(workspace) : null;
  }

  getWorkspace() {
    return this.workspaceDetailsSubject.getValue();
  }

  updateWorkspaceProfile(payload: UpdateWorkspaceProfilePayload) {
    this.isLoadingSubject.next(true);
    return this.http
      .patch<ApiResponse<WorkspaceDto>>(WORKSPACE_URL, payload)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  uploadWorkspaceLogo(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    this.isLoadingSubject.next(true);
    return this.http
      .post<ApiResponse<{ url: string }>>(`${WORKSPACE_URL}/logo`, formData)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  clearWorkspace() {
    localStorage.removeItem(WORKSPACE_DETAILS_KEY);
    this.workspaceDetailsSubject.next(null);
  }
}
